import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { USD, fullDate, magnitude } from '../lib/format';
import { C, ink } from '../lib/theme';
import type { FalsePositiveCandidate, FalsePositiveReviewStatus } from '../lib/types';
import { ScaleModal, type Origin } from './ScaleModal';
import { IconCheck, IconExternal, IconUndo } from './icons';
import { EmptyState, ErrorBanner, Skeleton } from './primitives';

const STATUS_TONE: Record<'pendente' | FalsePositiveReviewStatus, { color: string; label: string }> = {
  pendente: { color: C.amber, label: 'Pendente' },
  confirmado: { color: C.green, label: 'Confirmado' },
  descartado: { color: ink(0.45), label: 'Descartado' },
};

function Row({ c, onReview, busy }: {
  c: FalsePositiveCandidate;
  onReview: (id: number, status: FalsePositiveReviewStatus) => void;
  busy: boolean;
}) {
  const status = c.review_status ?? 'pendente';
  const tone = STATUS_TONE[status];
  const stripeUrl = c.nova_sub_id ? `https://dashboard.stripe.com/subscriptions/${c.nova_sub_id}` : null;
  const dias = Math.round((new Date(c.nova_sub_criada).getTime() - new Date(c.event_date).getTime()) / 86_400_000);

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 13,
      background: ink(0.04), border: `1px solid ${ink(0.09)}`,
    }}>
      <span style={{
        width: 8, height: 8, borderRadius: '50%', background: tone.color, flex: 'none',
        boxShadow: `0 0 8px ${tone.color}99`,
      }} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 14, fontWeight: 750, color: C.fg }}>{c.master_id}</span>
          <span style={{ fontSize: 11.5, color: ink(0.42) }}>{c.product_category}</span>
        </div>
        <div style={{ fontSize: 12, color: ink(0.46), marginTop: 2 }}>
          churn {fullDate(c.event_date)} · reassinou {fullDate(c.nova_sub_criada)} ({dias} dia{dias === 1 ? '' : 's'} depois)
        </div>
      </div>
      <div style={{
        fontSize: 13.5, fontWeight: 750, color: C.churn, fontVariantNumeric: 'tabular-nums', flex: 'none', minWidth: 66, textAlign: 'right',
      }}>{USD(magnitude(c.mrr_lost))}</div>

      {stripeUrl && (
        <button
          type="button" onClick={() => window.open(stripeUrl, '_blank', 'noopener,noreferrer')}
          title="Abrir a nova assinatura no Stripe"
          style={{
            width: 29, height: 29, borderRadius: 9, flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '1px solid rgba(74,125,255,0.4)', background: 'rgba(74,125,255,0.15)', color: C.blueSoft,
            cursor: 'pointer', font: 'inherit',
          }}
        ><IconExternal size={13} /></button>
      )}

      <span style={{
        fontSize: 10.5, fontWeight: 750, padding: '3px 10px', borderRadius: 99, flex: 'none',
        background: `${tone.color}1e`, color: tone.color, border: `1px solid ${tone.color}45`,
      }}>{tone.label}</span>

      <div style={{ display: 'flex', gap: 6, flex: 'none' }}>
        <button
          type="button" disabled={busy} onClick={() => onReview(c.id, 'confirmado')}
          title="Confirmar como falso positivo — conta nos KPIs da Visão Geral"
          style={{
            width: 29, height: 29, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: status === 'confirmado' ? `1px solid ${C.green}` : `1px solid ${ink(0.16)}`,
            background: status === 'confirmado' ? 'rgba(46,204,113,0.22)' : ink(0.06),
            color: status === 'confirmado' ? C.green : ink(0.6),
            cursor: busy ? 'wait' : 'pointer', font: 'inherit', opacity: busy ? 0.6 : 1,
          }}
        ><IconCheck size={13} /></button>
        <button
          type="button" disabled={busy} onClick={() => onReview(c.id, 'descartado')}
          title="Descartar — não é falso positivo, nada muda no evento"
          style={{
            width: 29, height: 29, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: status === 'descartado' ? `1px solid ${ink(0.32)}` : `1px solid ${ink(0.16)}`,
            background: status === 'descartado' ? ink(0.12) : ink(0.06),
            color: status === 'descartado' ? ink(0.85) : ink(0.6),
            cursor: busy ? 'wait' : 'pointer', font: 'inherit', opacity: busy ? 0.6 : 1,
          }}
        ><IconUndo size={13} /></button>
      </div>
    </div>
  );
}

/**
 * Zona de Falso Positivo (Scale Modal nível 2) — churns confirmados cujo
 * master_id+categoria ganhou uma assinatura ACTIVE nova depois do evento
 * (detecção dinâmica contra nekt_sync.assinaturas_raw, ver
 * false-positives.service.ts). Conferência é sempre manual: "Confirmar"
 * marca de verdade (entra no KPI da Visão Geral), "Descartar" só tira da
 * fila de revisão — não muda nada no evento original.
 */
export function FalsePositiveModal({ open, origin, onClose, period }: {
  open: boolean;
  origin: Origin | null;
  onClose: () => void;
  /** Escopa a lista ao período ativo da tela — ver `FalsePositiveButton`. */
  period?: { from?: string; to?: string };
}) {
  const { data, loading, error, reload } = useAsync(
    () => (open ? api.falsePositives(period) : Promise.resolve([])),
    [open, period?.from, period?.to],
  );
  const items = data ?? [];
  const pendentes = items.filter((c) => !c.review_status);
  const revisados = items.filter((c) => c.review_status);

  async function review(id: number, status: FalsePositiveReviewStatus) {
    await api.reviewFalsePositive(id, status);
    reload();
  }

  return (
    <ScaleModal
      open={open} origin={origin} onClose={onClose}
      title="Falso positivo — churns que reassinaram depois" width={720}
    >
      {loading && <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{[0, 1, 2].map((i) => <Skeleton key={i} height={62} radius={13} />)}</div>}
      {error && <ErrorBanner message={error} onRetry={reload} />}

      {!loading && !error && items.length === 0 && (
        <EmptyState
          tone="good" icon={<IconCheck size={22} />} title="Nada pra conferir"
          text="Nenhum churn confirmado com sinal de reassinatura no momento."
        />
      )}

      {!loading && !error && items.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {pendentes.length > 0 && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: C.amber, marginBottom: 9 }}>
                Pendentes · {pendentes.length}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {pendentes.map((c) => <Row key={c.id} c={c} onReview={review} busy={loading} />)}
              </div>
            </div>
          )}
          {revisados.length > 0 && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: ink(0.42), marginBottom: 9 }}>
                Já revisados · {revisados.length}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {revisados.map((c) => <Row key={c.id} c={c} onReview={review} busy={loading} />)}
              </div>
            </div>
          )}
        </div>
      )}
    </ScaleModal>
  );
}
